import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FiArrowUpCircle, FiX, FiEye, FiEyeOff } from "react-icons/fi";
import { toast } from "react-toastify";
import { useTranslation, Trans } from "react-i18next";
import Axios from "../../../../../_services/caller.service";
import styles from "./PromoteToSupportModal.module.scss";

const PromoteToSupportModal = ({ user, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canConfirm =
    password.length > 0 && password2.length > 0 && password === password2;

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setLoading(true);
    setError("");

    try {
      const res = await Axios.post("/promoteUserToSupportTeam", {
        userId: user.id,
        adminPassword: password,
      });
      toast.success(res.data.message || t("support.promote_success"));
      onSuccess(user.id);
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.message || t("support.promote_error");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button
          className={`${styles.closeBtn} has-tooltip`}
          onClick={onClose}
          type="button"
          aria-label={t("support.close")}
        >
          <FiX />
        </button>

        <div className={styles.iconWrap}>
          <FiArrowUpCircle className={styles.icon} />
        </div>

        <h2 className={styles.title}>{t("support.promote_title")}</h2>

        {step === 1 && (
          <>
            <div className={styles.userCard}>
              <span className={styles.label}>{t("support.user_label")}</span>
              <span className={styles.value}>{user.name}</span>
              <span className={styles.label}>{t("support.email_label")}</span>
              <span className={styles.value}>{user.email}</span>
            </div>

            <div className={styles.warningBox}>
              <Trans i18nKey="support.warning_text" components={{ strong: <strong />, em: <em /> }} />
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnCancel}
                onClick={onClose}
              >
                {t("support.cancel")}
              </button>
              <button
                type="button"
                className={styles.btnNext}
                onClick={() => setStep(2)}
              >
                {t("support.continue")}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className={styles.message}>
              <Trans i18nKey="support.confirm_password_message" components={{ strong: <strong /> }} />
            </p>

            <div className={styles.fieldGroup}>
              <label>{t("support.password_label")}</label>
              <div className={styles.passwordWrap}>
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("support.password_placeholder")}
                  autoFocus
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPwd((v) => !v)}
                  tabIndex={-1}
                >
                  {showPwd ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label>{t("support.confirm_password_label")}</label>
              <div className={styles.passwordWrap}>
                <input
                  type={showPwd2 ? "text" : "password"}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  placeholder={t("support.confirm_password_placeholder")}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPwd2((v) => !v)}
                  tabIndex={-1}
                >
                  {showPwd2 ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            {password2 && password !== password2 && (
              <span className={styles.fieldError}>
                {t("support.passwords_mismatch")}
              </span>
            )}

            {error && <span className={styles.fieldError}>{error}</span>}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnCancel}
                onClick={() => {
                  setStep(1);
                  setPassword("");
                  setPassword2("");
                  setError("");
                }}
              >
                {t("support.back")}
              </button>
              <button
                type="button"
                className={styles.btnConfirm}
                onClick={handleConfirm}
                disabled={!canConfirm || loading}
              >
                {loading ? t("support.verifying") : t("support.confirm_promotion")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PromoteToSupportModal;
